/**
 * ADR-0240 Slice 2a — typed client for the agency-referral endpoints.
 *
 *   GET  /v1/agency/referral  — read the slug + click aggregates +
 *                                attributed accounts.
 *   PUT  /v1/agency/referral  — set / rotate the slug (409 slug_taken,
 *                                400 invalid_shape / slug_reserved).
 *
 * Discriminated-union results mirror the Clients-page client in
 * `agency.ts`.
 */

import { ApiError, type HttpClient } from './http-client.js';

export interface AgencyReferralAttributedAccountWire {
  readonly account_id: string;
  /** NULL when the agency does NOT have a current link to this
   *  attributed client. The agency gets attribution regardless (that
   *  is what the row proves) but only sees the human-readable name
   *  when a current agency_client_link exists. */
  readonly name: string | null;
  readonly is_linked_client: boolean;
  readonly created_at: string;
  readonly primary_domain: string | null;
  readonly home_variant: string | null;
}

export interface AgencyReferralStateWire {
  readonly slug: string | null;
  readonly go_url: string | null;
  readonly clicks_total_7d: number;
  readonly clicks_total_30d: number;
  readonly signups_total: number;
  readonly attributed_accounts: readonly AgencyReferralAttributedAccountWire[];
  /** True when `signups_total` exceeds the server-side list cap (200).
   *  Dashboard renders "+ N more" when this is true. */
  readonly attributed_accounts_truncated: boolean;
}

export type GetAgencyReferralResult =
  | { readonly kind: 'ok'; readonly state: AgencyReferralStateWire }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function getAgencyReferral(http: HttpClient): Promise<GetAgencyReferralResult> {
  try {
    const res = await http.request<AgencyReferralStateWire>({
      method: 'GET',
      path: '/v1/agency/referral',
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', state: res };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'forbidden') {
      return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type PutAgencyReferralResult =
  | { readonly kind: 'ok'; readonly slug: string; readonly go_url: string }
  | { readonly kind: 'invalid_shape' }
  | { readonly kind: 'slug_reserved' }
  | { readonly kind: 'slug_taken' }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function putAgencyReferral(
  http: HttpClient,
  slug: string,
): Promise<PutAgencyReferralResult> {
  try {
    const res = await http.request<{ slug: string; go_url: string }>({
      method: 'PUT',
      path: '/v1/agency/referral',
      body: { slug },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', slug: res.slug, go_url: res.go_url };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'conflict' && err.server_reason === 'slug_taken') {
        return { kind: 'slug_taken' };
      }
      if (err.kind === 'bad_request') {
        if (err.server_reason === 'slug_reserved') return { kind: 'slug_reserved' };
        return { kind: 'invalid_shape' };
      }
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

function explainApiError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.server_reason ?? err.kind;
  }
  return err instanceof Error ? err.message : 'unknown_error';
}
